import React, { useEffect, useState, createContext, useContext } from 'react';
import { MapPinIcon, ClockIcon, BellIcon, CalendarDaysIcon, NetworkIcon, SparklesIcon, UsersIcon, BoxIcon } from 'lucide-react';
import { useEvent } from './EventContext';
export interface QuickLink {
  id: string;
  icon: BoxIcon;
  labelKey: string;
  page: string;
}
interface QuickLinksContextType {
  quickLinks: QuickLink[];
  availableLinks: QuickLink[];
  updateQuickLinks: (links: QuickLink[]) => void;
  resetToDefaults: () => void;
}
const QuickLinksContext = createContext<QuickLinksContextType | undefined>(undefined);
// All available navigation options (excluding home)
const ALL_AVAILABLE_LINKS: QuickLink[] = [{
  id: 'exhibitors',
  icon: UsersIcon,
  labelKey: 'nav.exhibitors',
  page: 'exhibitors'
}, {
  id: 'floorplan',
  icon: MapPinIcon,
  labelKey: 'nav.floorplan',
  page: 'floorplan'
}, {
  id: 'agenda',
  icon: ClockIcon,
  labelKey: 'nav.agenda',
  page: 'agenda'
}, {
  id: 'whatson',
  icon: SparklesIcon,
  labelKey: 'nav.whatson',
  page: 'whatson'
}, {
  id: 'schedule',
  icon: CalendarDaysIcon,
  labelKey: 'home.schedule',
  page: 'favorites'
}, {
  id: 'notifications',
  icon: BellIcon,
  labelKey: 'home.notifications',
  page: 'alerts'
}, {
  id: 'networking',
  icon: NetworkIcon,
  labelKey: 'nav.networking',
  page: 'networking'
}, {
  id: 'buyers',
  icon: UsersIcon,
  labelKey: 'nav.buyers',
  page: 'buyers'
}];
// Default quick links for different events
const DEFAULT_QUICK_LINKS: Record<string, string[]> = {
  industry: ['networking', 'agenda', 'schedule', 'notifications'],
  active: ['whatson', 'agenda', 'schedule', 'notifications'],
  muscle: ['whatson', 'agenda', 'schedule', 'notifications'],
  default: ['floorplan', 'agenda', 'schedule', 'notifications']
};
export function QuickLinksProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    currentEvent
  } = useEvent();
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>([]);
  // Load quick links for current event
  useEffect(() => {
    const storageKey = `quickLinks_${currentEvent.id}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const savedIds = JSON.parse(saved) as string[];
        const links = savedIds.map(id => ALL_AVAILABLE_LINKS.find(link => link.id === id)).filter((link): link is QuickLink => link !== undefined);
        setQuickLinks(links);
      } catch (e) {
        // If parsing fails, use defaults
        loadDefaults();
      }
    } else {
      // No saved preferences, use defaults
      loadDefaults();
    }
  }, [currentEvent.id]);
  const loadDefaults = () => {
    const defaultIds = DEFAULT_QUICK_LINKS[currentEvent.id] || DEFAULT_QUICK_LINKS.default;
    const links = defaultIds.map(id => ALL_AVAILABLE_LINKS.find(link => link.id === id)).filter((link): link is QuickLink => link !== undefined);
    setQuickLinks(links);
  };
  const updateQuickLinks = (links: QuickLink[]) => {
    setQuickLinks(links);
    const storageKey = `quickLinks_${currentEvent.id}`;
    localStorage.setItem(storageKey, JSON.stringify(links.map(l => l.id)));
  };
  const resetToDefaults = () => {
    const storageKey = `quickLinks_${currentEvent.id}`;
    localStorage.removeItem(storageKey);
    loadDefaults();
  };
  // Get available links (all links not currently in quick links)
  const availableLinks = ALL_AVAILABLE_LINKS.filter(link => !quickLinks.find(ql => ql.id === link.id));
  return <QuickLinksContext.Provider value={{
    quickLinks,
    availableLinks,
    updateQuickLinks,
    resetToDefaults
  }} data-id="element-4270">
      {children}
    </QuickLinksContext.Provider>;
}
export function useQuickLinks() {
  const context = useContext(QuickLinksContext);
  if (!context) {
    throw new Error('useQuickLinks must be used within QuickLinksProvider');
  }
  return context;
}